import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'groupedStackedBar',
  name_en: 'Grouped Stacked Bar',
  // name_zh: '分组堆叠柱状图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.STR, DEFINE.DATE],
      category: DEFINE.SINGLE,
      priority: DEFINE.DATE
    },
    y: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    group: {
      itemType: [DEFINE.STR, DEFINE.DATE],
      category: DEFINE.SINGLE
    },
    group2: {
      itemType: [DEFINE.STR, DEFINE.DATE],
      category: DEFINE.SINGLE
    }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string;
    group: string;
    group2: string;
  };
}

export const layer = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  // eslint-disable-next-line max-lines-per-function
  render(api, spec, $dom) {
    const { layout, data: dataArray, util, hook, token } = api;
    const {
      dataIndex = 0,
      encoding: { x, y, group, group2 }
    } = spec;
    const presetOption = hook.buildStandardChartPresetOption(api);
    const data = dataArray[dataIndex];
    const dataMetaInfo = hook.parseDataMetaInfo(data);

    // * -------------------------- data
    const { xAxisData, group1Names, group2Names } = util.dataEncoding.encoding(
      data.values.slice(),
      {
        xAxisData: {
          field: x,
          transforms: [{ type: 'deDuplication' }, { type: 'sort', order: 'ascending' }]
        },
        group1Names: {
          field: group,
          transforms: [
            { type: 'deDuplication' },
            { type: 'filter', cb: val => typeof val !== 'undefined' }
          ]
        },
        group2Names: {
          field: group2,
          transforms: [
            { type: 'deDuplication' },
            { type: 'filter', cb: val => typeof val !== 'undefined' }
          ]
        }
      }
    );

    const series = [] as { type: string; name: string; data: unknown[]; stack: string }[];
    (group1Names as string[]).forEach(groupName => {
      series.push({
        name: groupName,
        type: 'bar',
        stack: 'group1',
        data: xAxisData.map(xAxisItem => {
          const dataItem = util.dataTransform.transform(data.values, [
            {
              type: 'find',
              cb: item => item[x] === xAxisItem && item[group] === groupName
            }
          ]);

          // @ts-ignore
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return dataItem?.[y];
        })
      });
    });
    (group2Names as string[]).forEach(groupName => {
      series.push({
        name: groupName,
        type: 'bar',
        stack: 'group2',
        data: xAxisData.map(xAxisItem => {
          const dataItem = util.dataTransform.transform(data.values, [
            {
              type: 'find',
              cb: item => item[x] === xAxisItem && item[group2] === groupName
            }
          ]);

          // @ts-ignore
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return dataItem?.[y];
        })
      });
    });
    const group1Series = util.dataTransform.transform(series, [
      { type: 'filter', cb: item => item.stack === 'group1' }
    ]) as unknown as typeof series;
    const group2Series = util.dataTransform.transform(series, [
      { type: 'filter', cb: item => item.stack === 'group2' }
    ]) as unknown as typeof series;
    let group1MaxNum;
    let group2MaxNum;
    group1Series.forEach((item, index) => {
      if (!index) {
        group1MaxNum = item.data.slice();
        return;
      }
      group1MaxNum.forEach((_, idx) => {
        group1MaxNum[idx] += item.data[idx];
      });
    });
    group2Series.forEach((item, index) => {
      if (!index) {
        group2MaxNum = item.data.slice();
        return;
      }
      group2MaxNum.forEach((_, idx) => {
        group2MaxNum[idx] += item.data[idx];
      });
    });
    // 增加容错处理，当group2MaxNum为空或是undefined时（第二组柱子内容为空时）
    group2MaxNum = group2MaxNum ?? [];
    group1MaxNum = group1MaxNum ?? [];

    // * -------------------------- chart
    const chartViewRect = $dom.getBoundingClientRect();
    const baseGridOption = Array.isArray(presetOption.grid)
      ? presetOption.grid[0]
      : presetOption.grid!;
    const grid = {
      ...baseGridOption
    };
    const { showDataZoom, dataZoomLimit: limit } = hook
      .layout(api)
      .isShowDataZoom(layout, xAxisData.length);
    const dataZoomStartIndex = series[0].data.length - limit;
    const dataZoomEndIndex = series[0].data.length - 1;
    const barWidth =
      ((chartViewRect.width - grid.left - grid.right) / series[0].data.length / 2) * 0.8;
    const barMaxWidth = util.const.BAR_MAX_WIDTH;
    series.forEach(item => {
      item.barMaxWidth = barWidth > barMaxWidth ? barMaxWidth : undefined;
    });
    return {
      grid: {
        ...grid,
        bottom: showDataZoom ? 48 : grid.bottom
      },
      tooltip: {
        trigger: 'axis'
      },
      xAxis: {
        show: true,
        dvAlignEdge: true,
        type: 'category',
        name: x + (dataMetaInfo[x]?.unit ? `(${dataMetaInfo[x].unit})` : ''),
        data: xAxisData,
        axisPointer: {
          type: 'shadow',
          shadowStyle: {
            dvMaxWidth:
              barWidth > barMaxWidth ? barMaxWidth * util.const.SHADOW_WIDTH_RATIO * 2 : undefined
          },
          z: 0
        }
      },
      yAxis: {
        show: true,
        dvSplitLineNumber: hook.getSplitLineNumber(chartViewRect.height - grid.top - grid.bottom),
        type: 'value',
        name: y + (dataMetaInfo[y]?.unit ? `(${dataMetaInfo[y].unit})` : ''),
        z: undefined
      },
      dataZoom: showDataZoom
        ? [
            {
              type: 'slider',
              show: showDataZoom,
              height: 16,
              bottom: 8,
              right: 8,
              startValue: dataZoomStartIndex,
              endValue: dataZoomEndIndex,
              moveHandleIcon: 'path://',
              moveHandleSize: -32,
              moveHandleStyle: {
                opacity: 0
              },
              dvMoveHandleOffset: -16,
              borderRadius: 1,
              handleSize: '100%',
              dataBackground: {
                areaStyle: {
                  opacity: 0
                },
                lineStyle: {
                  opacity: 0
                }
              },
              selectedDataBackground: {
                areaStyle: {
                  opacity: 0
                },
                lineStyle: {
                  opacity: 0
                }
              }
            }
          ]
        : presetOption.dataZoom,
      series
    };
  }
});
